const DEFAULT_OPTIONS = {
    dateFields: ['paymentDate', 'date'],
    amountFields: ['paymentAmount', 'amount'],
    amountBaseSuffix: ['BaseCurrency', 'BaseCurrency'], // Suffixes for base currency fields
    // The sign each side's amount is read with. An expense is matched
    // against money going OUT, which a bank transaction stores as a
    // negative amount: [1, -1] compares the two as magnitudes.
    amountSigns: [1, 1],
    // A field read when neither amount field is set — an expense booked
    // before it carried an amountDue is still owed its gross total.
    amountFallbackFields: [null, null],
    idField: 'id'
};

// The amount an item is compared on: its base-currency field when it has one,
// otherwise the field itself, otherwise the fallback (base first, again)
const getAmount = (item, fieldName, baseSuffix, sign = 1, fallback = null) => {
    const baseFieldName = fieldName + baseSuffix;
    const value = item[baseFieldName] !== undefined ? item[baseFieldName]
        : item[fieldName] !== undefined ? item[fieldName]
        : (fallback ? item[fallback + baseSuffix] ?? item[fallback] : undefined);
    return sign * value;
};

/**
 * The amount one side compares an item on, signed as the comparison reads it —
 * `side` 0 is the primary list (payments), 1 the secondary (transactions).
 */
export function amountOf(item, side, customOptions = {}) {
    const o = { ...DEFAULT_OPTIONS, ...customOptions };
    return getAmount(item, o.amountFields[side], o.amountBaseSuffix[side], o.amountSigns[side], o.amountFallbackFields[side]);
}

/**
 * Several primary items as one: the amounts they would each be compared on,
 * summed, dated at the earliest of them. Three instalments paid by one bank
 * transfer are matched by running the search against this row.
 *
 * The sum is written to the base-currency field, which the comparison reads
 * first, and unsigned — the comparison applies the sign itself.
 */
export function combineRows(rows, customOptions = {}) {
    const o = { ...DEFAULT_OPTIONS, ...customOptions };
    const [dateField] = o.dateFields;
    const [amountField] = o.amountFields;
    const [baseSuffix] = o.amountBaseSuffix;
    const [fallback] = o.amountFallbackFields;

    const total = rows.reduce((sum, row) => sum + (Number(getAmount(row, amountField, baseSuffix, 1, fallback)) || 0), 0);
    const dates = rows.map(row => row[dateField]).filter(Boolean).sort((a, b) => new Date(a) - new Date(b));

    return {
        [o.idField]: rows.map(row => row[o.idField] ?? row.id).join('+'),
        [dateField]: dates[0],
        [amountField]: total,
        [amountField + baseSuffix]: total,
    };
}

/**
 * Finds all relevant transaction/payment suggestions, prioritizing base currency amounts for comparison
 * if configured, and falling back to the standard amount fields.
 *
 * @param {Array<Object>} primaryList - The list being matched (e.g., Payments).
 * @param {Array<Object>} secondaryList - The list of potential matches (e.g., Transactions).
 * @param {number} primaryDaysTolerance - The tolerance for Tier 1 (Exact Match).
 * @param {number} minAmountMatchPercentage - Minimum percentage match for amounts (used as a filter).
 * @param {Object} customOptions - Field configuration.
 * @returns {Object<string, Array<Object>>} An object mapping primary item ID to a sorted array of ALL secondary items.
 */
function findSuggestions(primaryList, secondaryList, primaryDaysTolerance = 7, minAmountMatchPercentage = 95, customOptions = {}) {
    // Guard against null/undefined inputs - return empty suggestions object
    if (!Array.isArray(primaryList) || !Array.isArray(secondaryList)) {
        console.warn('findSuggestions: Invalid input - primaryList and secondaryList must be arrays', {
            primaryListType: Array.isArray(primaryList) ? 'array' : typeof primaryList,
            secondaryListType: Array.isArray(secondaryList) ? 'array' : typeof secondaryList
        });
        return {};
    }
    
    // Handle empty arrays gracefully
    if (primaryList.length === 0 || secondaryList.length === 0) {
        console.log('findSuggestions: Empty input array(s)', {
            primaryListLength: primaryList.length,
            secondaryListLength: secondaryList.length
        });
        return {};
    }

    const options = { ...DEFAULT_OPTIONS, ...customOptions };

    // Deconstruct fields for clarity
    const [primaryDateField, secondaryDateField] = options.dateFields;
    const [primaryAmountField, secondaryAmountField] = options.amountFields;
    const [primaryBaseSuffix, secondaryBaseSuffix] = options.amountBaseSuffix;
    const [primarySign, secondarySign] = options.amountSigns;
    const [primaryFallback, secondaryFallback] = options.amountFallbackFields;
    const secondaryIdField = options.idField; 
    
    // --- Constants & Dynamic Calculations ---
    const MS_PER_DAY = 1000 * 60 * 60 * 24;
    const MAX_CONFIDENCE_DAYS = 60; 
    const WIDER_DAYS_TOLERANCE = primaryDaysTolerance * 2; 
    const ABSOLUTE_TOLERANCE_FOR_TIER1 = 0.01;
    const minMatchRatio = minAmountMatchPercentage / 100;

    console.log('secondary List: ', secondaryList)

    const secondaryMap = new Map(secondaryList.map(t => [t[secondaryIdField], { ...t, consumed: false }]));
    const suggestions = {};

    // --- Helper Functions ---

    const isAmountTolerant = (primaryAmount, secondaryAmount) => {
        const minRequiredAmount = primaryAmount * minMatchRatio;
        const maxRequiredAmount = primaryAmount * 1.05; 
        // All checks are now on the stable base/fallback amounts
        return secondaryAmount >= minRequiredAmount && secondaryAmount <= maxRequiredAmount;
    };

    const areDatesClose = (date1, date2, toleranceDays) => {
        const d1 = new Date(date1).getTime();
        const d2 = new Date(date2).getTime();
        return Math.abs(d1 - d2) / MS_PER_DAY <= toleranceDays;
    };

    // Confidence Score Calculation: Combined Date Proximity and Amount Match Ratio (using base/fallback amounts)
    const calculateScore = (primaryItem, secondaryItem) => {
        const primaryDate = primaryItem[primaryDateField];
        const secondaryDate = secondaryItem[secondaryDateField];
        
        // Use the new helper to get amounts
        const primaryAmount = getAmount(primaryItem, primaryAmountField, primaryBaseSuffix, primarySign, primaryFallback);
        const secondaryAmount = getAmount(secondaryItem, secondaryAmountField, secondaryBaseSuffix, secondarySign, secondaryFallback);
        
        const d1 = new Date(primaryDate).getTime();
        const d2 = new Date(secondaryDate).getTime();
        const dateDiffDays = Math.abs(d1 - d2) / MS_PER_DAY;

        if (dateDiffDays > MAX_CONFIDENCE_DAYS || primaryAmount === 0) return 0; 

        // 1. Date Proximity Score
        const dateProximityScore = (MAX_CONFIDENCE_DAYS - dateDiffDays) / MAX_CONFIDENCE_DAYS;

        // 2. Amount Match Ratio (Capped at 1.0)
        const amountRatio = Math.min(secondaryAmount / primaryAmount, 1.0); 

        // 3. Combined Confidence Score
        return dateProximityScore * amountRatio;
    };

    // Metric for sorting TIER 99 (Unmatched) items
    const calculateDistanceMetric = (primaryItem, secondaryItem) => {
        const primaryDateMs = new Date(primaryItem[primaryDateField]).getTime();
        const secondaryDateMs = new Date(secondaryItem[secondaryDateField]).getTime();
        
        // Use the new helper to get amounts
        const primaryAmount = getAmount(primaryItem, primaryAmountField, primaryBaseSuffix, primarySign, primaryFallback);
        const secondaryAmount = getAmount(secondaryItem, secondaryAmountField, secondaryBaseSuffix, secondarySign, secondaryFallback);

        if (primaryAmount === 0) return Infinity; 
        
        // Relative Amount Difference: |(Primary - Secondary) / Primary|
        const relativeAmountDiff = Math.abs(primaryAmount - secondaryAmount) / primaryAmount;
        
        // Days Difference
        const daysDiff = Math.abs(primaryDateMs - secondaryDateMs) / MS_PER_DAY;
        
        return relativeAmountDiff * daysDiff;
    };
    
    // --- 1. TIER 1: EXACT MATCH & GREEDY CONSUMPTION ---
    for (const primaryItem of primaryList) {
        const primaryId = primaryItem[options.idField] || primaryItem.id;
        const primaryDate = primaryItem[primaryDateField];
        
        let exactMatch = null;
        let exactMatchScore = -1;
        
        // Amounts used for this iteration (using base currency if available)
        const primaryAmount = getAmount(primaryItem, primaryAmountField, primaryBaseSuffix, primarySign, primaryFallback);

        for (const secondaryItem of secondaryMap.values()) {
            if (secondaryItem.consumed) continue;

            const secondaryAmount = getAmount(secondaryItem, secondaryAmountField, secondaryBaseSuffix, secondarySign, secondaryFallback);
            const secondaryDate = secondaryItem[secondaryDateField];

            const isExactAmount = Math.abs(primaryAmount - secondaryAmount) <= ABSOLUTE_TOLERANCE_FOR_TIER1;
            const isCloseDate = areDatesClose(primaryDate, secondaryDate, primaryDaysTolerance); 

            if (isExactAmount && isCloseDate) {
                const score = calculateScore(primaryItem, secondaryItem); 
                
                if (score > exactMatchScore) {
                    exactMatchScore = score;
                    exactMatch = secondaryItem;
                }
            }
        }

        if (exactMatch) {
            exactMatch.consumed = true;
            const secondaryAmount = getAmount(exactMatch, secondaryAmountField, secondaryBaseSuffix, secondarySign, secondaryFallback);

            suggestions[primaryId] = [{
                ...exactMatch,
                confidenceScore: exactMatchScore,
                matchTier: 1, 
                matchRatio: secondaryAmount / primaryAmount,
            }];
        } else {
            suggestions[primaryId] = [];
        }
    }

    // --- 2. SINGLE COMPREHENSIVE SUGGESTION SEARCH (Tolerant & Unmatched) ---
    for (const primaryItem of primaryList) {
        const primaryId = primaryItem[options.idField] || primaryItem.id;
        const primaryDate = primaryItem[primaryDateField];
        
        const currentSuggestionsMap = new Map();
        const unmatchedItems = [];
        
        if (suggestions[primaryId][0]?.matchTier === 1) {
            currentSuggestionsMap.set(suggestions[primaryId][0][secondaryIdField], suggestions[primaryId][0]);
        }
        
        // Amounts used for this iteration
        const primaryAmount = getAmount(primaryItem, primaryAmountField, primaryBaseSuffix, primarySign, primaryFallback);

        for (const secondaryItem of secondaryMap.values()) {
            if (secondaryItem.consumed) continue;

            const secondaryAmount = getAmount(secondaryItem, secondaryAmountField, secondaryBaseSuffix, secondarySign, secondaryFallback);
            const secondaryDate = secondaryItem[secondaryDateField];

            const score = calculateScore(primaryItem, secondaryItem);
            const isTolerant = isAmountTolerant(primaryAmount, secondaryAmount);
            
            // Tier 3 date check uses WIDER_DAYS_TOLERANCE as the hard filter
            const isCloseEnoughForSuggestion = areDatesClose(primaryDate, secondaryDate, WIDER_DAYS_TOLERANCE); 
            
            let enrichedItem;
            
            // Match is: Amount is tolerant AND Date is within WIDER_DAYS_TOLERANCE
            if (isTolerant && isCloseEnoughForSuggestion) {
                 enrichedItem = {
                    ...secondaryItem,
                    confidenceScore: score,
                    matchTier: 3, 
                    matchRatio: secondaryAmount / primaryAmount,
                };
            } else {
                 // Unmatched (Tier 99)
                 const distanceMetric = calculateDistanceMetric(primaryItem, secondaryItem);

                 enrichedItem = {
                    ...secondaryItem,
                    confidenceScore: 0,
                    matchTier: 99,
                    matchRatio: 0,
                    distanceMetric: distanceMetric
                };
                unmatchedItems.push(enrichedItem);
            }
            
            if (enrichedItem.matchTier !== 99) {
                 currentSuggestionsMap.set(enrichedItem[secondaryIdField], enrichedItem);
            }
        }

        const finalSuggestions = Array.from(currentSuggestionsMap.values());
        
        finalSuggestions.sort((a, b) => b.confidenceScore - a.confidenceScore);
        unmatchedItems.sort((a, b) => a.distanceMetric - b.distanceMetric);
        
        unmatchedItems.forEach(t => delete t.distanceMetric);
        
        suggestions[primaryId] = [...finalSuggestions, ...unmatchedItems];
    }

    return suggestions;
}

// --- Example Data ---

const payments = [
    { "paymentId": "INV-P-1", "paymentAmount": 100.00, "paymentDate": "2025-02-17T15:56:11.165Z" },
    { "paymentId": "INV-P-2", "paymentAmount": 500.00, "paymentDate": "2025-03-01T00:00:00.000Z" }, 
    { "paymentId": "INV-P-3", "paymentAmount": 200.00, "paymentDate": "2025-03-10T00:00:00.000Z" }
];

const transactions = [
    // T1: Exact match for INV-P-1 (Consumed by P1)
    { "id": "T-1", "date": "2025-02-17T15:56:11.165Z", "amount": 100.00, "description": "Exact Match for P1" },
    
    // T2: 10 days difference from P2 (Suggestion)
    { "id": "T-2", "date": "2025-03-11T00:00:00.000Z", "amount": 480.00, "description": "Wide Match for P2" }, 

    // T3: 3 days difference from P3 (Suggestion)
    { "id": "T-3", "date": "2025-03-13T00:00:00.000Z", "amount": 195.00, "description": "Close Match for P3" },

    // T4: 15 days difference from P3 (Too far for score > 0) -> Unmatched, Date close
    { "id": "T-4", "date": "2025-03-25T00:00:00.000Z", "amount": 195.00, "description": "Too Far, Close Amount" },

    // T5: 5 days difference from P3 (Suggestion)
    { "id": "T-5", "date": "2025-03-15T00:00:00.000Z", "amount": 199.00, "description": "Close Match for P3" },

    // T6: Unmatched, Date far, Amount far. Should sort below T4.
    { "id": "T-6", "date": "2026-01-01T00:00:00.000Z", "amount": 999.00, "description": "Unrelated, Far Away" } 
];

// --- Execution ---
//const results = findSuggestions(payments, transactions, 7, 95);

//console.log('Payments:', payments);
//console.log(JSON.stringify(results, null, 2));

export default findSuggestions